/*
 * The third pane, drawn around whichever view was requested (UX §2.1, VISUAL.md §7.7). The frame is
 * the shell's — a 48 px header like the conversation band, with the view *group's* name, the view's
 * own aside and the close button; where the view has siblings, the tab strip in its track under
 * it; then the body — and the view renders only its body, so a thread and a profile are closed the
 * same way and a module never draws a chrome of its own.
 *
 * The strip is what lets About, Files and Pinned share one column without fighting for it (X-A4
 * N13): a view names a `group`, and when two or more registered views of that group answer
 * `relevant` for the open request's params, they are drawn as tabs — one tab stop, the arrow keys
 * between them — and a tab click keeps the params and swaps the view. The heading names the group
 * and not the tab ("Conversation details" over About · Files · Pinned), so the column is headed the
 * same whichever tab is open and the tabs carry the short names (D-06 S6).
 *
 * Below 1200 px the same view is a dialog, and there the title and the close button are the
 * dialog's: the pane then draws its body alone and says so by taking no `onClose`. Both titles are
 * an `h2`, so one view is not 15 px in the column and 22 px in the sheet (X-A3 P10).
 *
 * The heading is focusable, and it is where the shell puts the reader when the column opens: the
 * column itself took the focus before, which painted a 2 px accent outline around all 360 px of it
 * (X-A3 P11).
 */

import { Suspense, useMemo, type KeyboardEvent } from "react"
import { Button, ListState } from "../../ui/index.ts"
import { t, translate } from "../i18n/index.ts"
import type { DetailsView } from "../modules/registry.ts"
import { useSlot } from "../modules/slots.ts"
import "./details.css"
import { useDetails, type DetailsRequest } from "./details.tsx"
import { Icon } from "./icons.tsx"
import "./shell.css"

interface DetailsPaneProps {
	view: DetailsView
	params: DetailsRequest["params"]
	/** Given in the column, where the pane carries its own header; omitted inside the dialog. */
	onClose?: () => void
}

/**
 * The views drawn as tabs beside `view`: its group's registered views that have something to show
 * for these params, in their order. The open view is always among them, so a strip never lacks
 * the tab that is selected; fewer than two is no strip at all.
 */
function tabsFor(
	view: DetailsView,
	registered: readonly DetailsView[],
	params: DetailsRequest["params"],
): DetailsView[] {
	if (view.group === undefined) return []
	const siblings = registered.filter((other) => other.id !== view.id)
	return [view, ...siblings]
		.filter((entry) => entry.group === view.group && (entry.relevant?.(params) ?? true))
		.sort((a, b) => (a.order ?? 0) - (b.order ?? 0) || a.id.localeCompare(b.id))
}

/** The one panel of the strip: the open view's body, whichever view that is. */
const PANEL_ID = "tb-details-panel"

function tabId(viewId: string): string {
	return `tb-details-tab-${viewId.replaceAll(".", "-")}`
}

function DetailsTabs({
	tabs,
	current,
	panelId,
	onSelect,
}: {
	tabs: DetailsView[]
	current: string
	/** The panel every tab of this strip controls; the views share one, because one is open at a time. */
	panelId: string
	onSelect: (viewId: string) => void
}) {
	// The keys sit on the tabs and not on the strip: the strip is a container and never takes
	// focus, and a key handler on it would be a control nobody can reach.
	const move =
		(at: number) =>
		(event: KeyboardEvent<HTMLButtonElement>): void => {
			const wanted: Record<string, number> = {
				ArrowRight: at + 1,
				ArrowDown: at + 1,
				ArrowLeft: at - 1,
				ArrowUp: at - 1,
				Home: 0,
				End: tabs.length - 1,
			}
			const next = wanted[event.key]
			if (next === undefined) return
			event.preventDefault()
			const tab = tabs[Math.max(0, Math.min(next, tabs.length - 1))]
			if (tab === undefined || tab.id === current) return
			onSelect(tab.id)
			document.getElementById(tabId(tab.id))?.focus()
		}
	return (
		<div className="tb-tabs tb-details__tabs" role="tablist" aria-label={t("shell.details.tabs")}>
			{tabs.map((tab, at) => (
				<button
					key={tab.id}
					id={tabId(tab.id)}
					type="button"
					role="tab"
					className="tb-tabs__item"
					aria-controls={panelId}
					aria-selected={tab.id === current}
					tabIndex={tab.id === current ? 0 : -1}
					onKeyDown={move(at)}
					onClick={() => onSelect(tab.id)}
				>
					{translate(tab.tabLabelKey ?? tab.titleKey)}
				</button>
			))}
		</div>
	)
}

export function DetailsPane({ view, params, onClose }: DetailsPaneProps) {
	const details = useDetails()
	const registered = useSlot("detailsViews")
	const tabs = useMemo(() => tabsFor(view, registered, params), [view, registered, params])
	const strip =
		tabs.length < 2 ? null : (
			<DetailsTabs
				tabs={tabs}
				current={view.id}
				panelId={PANEL_ID}
				/*
				 * `focus: false`: the column is already open and the reader is standing in its tab
				 * strip. An ordinary `open()` moves the focus to the pane's heading, which for a
				 * tab strip means an arrow key selects the next tab and then throws the reader out
				 * of the strip — so the strip would not be the one tab stop with the arrow keys
				 * inside it that a tablist has to be (WAI-ARIA), and `ArrowRight` twice would reach
				 * nothing. The tab keeps the focus, which is what selecting a tab does everywhere.
				 */
				onSelect={(viewId) => details.open({ view: viewId, params }, { focus: false })}
			/>
		)
	// A view may be a `lazy()` of a chunk that is not in this reader's browser yet (registry.ts
	// `SlotComponent`), so both shapes suspend inside the same skeleton: the column is opened by a
	// click and the fallback draws nothing for the first `--delay-reveal`, which is longer than a
	// cached chunk takes to resolve.
	const rendered = (
		<Suspense fallback={<ListState.Loading rows={3} />}>
			<view.View params={params} />
		</Suspense>
	)
	// With a strip, the body is the panel the selected tab controls, and it is named by that tab: a
	// reader told "tab, selected, 1 of 3" has somewhere to move to. Without one there are no tabs,
	// so there is no panel either — a lone `tabpanel` would be a region that belongs to nothing.
	const body =
		strip === null ? (
			<div className="tb-details__body">{rendered}</div>
		) : (
			<div
				role="tabpanel"
				id={PANEL_ID}
				aria-labelledby={tabId(view.id)}
				tabIndex={-1}
				className="tb-details__body"
			>
				{rendered}
			</div>
		)
	if (onClose === undefined) {
		return (
			<div className="tb-details tb-details--sheet">
				{strip}
				{body}
			</div>
		)
	}

	return (
		<aside className="tb-details" aria-label={t("shell.region.details")}>
			<div className="tb-details__header">
				<h2 className="tb-details__title" tabIndex={-1}>
					{translate(view.groupTitleKey ?? view.titleKey)}
				</h2>
				{view.Aside === undefined ? null : (
					<div className="tb-details__aside">
						{/* The aside sits in the header beside the heading, so it waits on nothing a
						    reader can see: an empty fallback keeps the 48 px row from moving. */}
						<Suspense fallback={null}>
							<view.Aside params={params} />
						</Suspense>
					</div>
				)}
				<Button
					variant="ghost"
					quiet
					icon
					size="sm"
					className="tb-details__close"
					aria-label={t("shell.details.close")}
					before={<Icon name="close" />}
					onClick={onClose}
				/>
			</div>
			{strip}
			{body}
		</aside>
	)
}
